import React from "react";
import moment from "moment";
import { connect } from "react-algoliasearch-helper";
import { Link } from "react-router";
import get from "lodash/get";
import striptags from "striptags";

import { intersperse } from "../../../util";
import SearchStore from "../../../stores/SearchStore";
import EventBucketStore from "../../../stores/EventBucketStore";

const CONTAINER_STYLE = {
  border: "1px solid #ccc",
  borderRadius: "5px",
  backgroundColor: "#fff",
  marginTop: 5,
  maxHeight: "80vh",
  overflowY: "scroll",
  overflowX: "hidden",
  boxShadow:
    "0 3px 20px rgba(89,105,129,.3),0 1px 2px rgba(0,0,0,.05),0 0 0 1px rgba(89,105,129,.1)"
};
const GOD_CONTAINER_STYLE = {
  maxHeight: "70vh"
};

const NO_RESULTS_STYLE = {
  textAlign: "center",
  padding: 10
};

const HIT_STYLE = {
  paddingTop: 15,
  paddingLeft: 15,
  paddingRight: 15,
  paddingBottom: 15,
  lineHeight: "1.1",
  borderBottom: "1px solid #ccc",
  cursor: "pointer"
};

const HINT_KEY_STYLE = {
  border: "1px solid #CFD7E6",
  borderRadius: 4,
  padding: "0px 5px",
  backgroundColor: "#fff",
  fontFamily: "monospace",
  margin: "0 2px"
};

const LAST_HIT_STYLE = Object.assign({}, HIT_STYLE, {
  borderBottom: "none"
});

const HIT_TITLE_STYLE = {
  fontWeight: "bold"
};
const HIT_SUBTITLE_STYLE = {
  fontSize: "0.8em"
};

class SearchResults extends React.Component {
  render() {
    let { helper, results, showResults, compact, godMode, condensedUsers } =
        this.props,
      resultTypes = SearchStore.getState().types;

    if (showResults && (!results || !results.query || results.query === "*")) {
      if (compact) return null;
      return (
        <div style={CONTAINER_STYLE}>
          <div style={NO_RESULTS_STYLE}>
            <div>
              <strong>Tip:</strong> Open search by pressing the{" "}
              <span style={HINT_KEY_STYLE}>/</span> key.
            </div>
            <div>
              Navigate results using <span style={HINT_KEY_STYLE}>↑</span>/
              <span style={HINT_KEY_STYLE}>↓</span>/
              <span style={HINT_KEY_STYLE}>Enter</span>.
            </div>
          </div>
        </div>
      );
    }

    return showResults && results && results.query ? (
      <div
        style={Object.assign(
          {},
          CONTAINER_STYLE,
          godMode && GOD_CONTAINER_STYLE
        )}
      >
        {results.hits.length === 0 && (
          <div style={NO_RESULTS_STYLE}>
            No results found for <em>"{results.query}"</em>
          </div>
        )}
        {results.hits.map((hit, i) => {
          if (!hit.name) {
            hit.name = hit.email;
          }

          // Typesense workaround: Due to api version differences, manually strip
          // matchLevel=none highlights from the results.
          Object.keys(hit._highlightResult).forEach(key => {
            if (
              (hit._highlightResult[key] &&
                hit._highlightResult[key].matchLevel === "none") ||
              Array.isArray(hit._highlightResult[key])
            ) {
              delete hit._highlightResult[key];
            }
          });

          let displayName;
          if (hit._type in resultTypes) {
            displayName = resultTypes[hit._type].display_name;
          } else {
            displayName = hit._type;
          }

          // Fallback name to id (e.g. for Ticket #123ABC)
          if (!hit.name && hit.id) {
            hit.name = `${displayName} #${hit.id.toUpperCase()}`;
          }

          // Fallback name to content (Note)
          if (!hit.name && hit.content) {
            hit.name = striptags(hit.content);
          }

          let titleContent;
          if (hit._highlightResult && hit._highlightResult.name) {
            titleContent = (
              <span
                dangerouslySetInnerHTML={{
                  __html: hit._highlightResult.name.value
                }}
              />
            );
          } else if (hit._highlightResult && hit._highlightResult.id) {
            titleContent = (
              <span>
                {displayName} #
                <span
                  dangerouslySetInnerHTML={{
                    __html: hit._highlightResult.id.value
                  }}
                />
              </span>
            );
          } else {
            titleContent = hit.name;
          }

          let subtitleKeys = hit._highlightResult
            ? Object.keys(hit._highlightResult).filter(
                k => ["name", "id", "content"].indexOf(k) === -1
              )
            : [];

          if (hit.person) {
            subtitleKeys.push(hit.person.name ? "person.name" : "person.email");
          }

          let extraSubtitleItems = [];
          if (hit.event_bucket) {
            let bucket = EventBucketStore.getEventBucketById(hit.event_bucket);
            if (bucket) {
              extraSubtitleItems.push(bucket.name);
            }
          }
          if (hit.start_time) {
            extraSubtitleItems.push(moment(hit.start_time).format("M/D/Y"));
          }

          return (
            <div
              key={hit.objectID}
              style={i === results.hits.length - 1 ? LAST_HIT_STYLE : HIT_STYLE}
              className={`GlobalSearchResults--hit ${
                i === this.props.selectedIndex ? "selected" : ""
              }`}
              onTouchTap={() => this.props.openHitByIndex(i)}
            >
              <div style={HIT_TITLE_STYLE}>{titleContent}</div>
              <div style={HIT_SUBTITLE_STYLE}>
                {displayName}
                {subtitleKeys.length || extraSubtitleItems.length
                  ? " · "
                  : null}
                {intersperse(
                  subtitleKeys
                    .map((key, i) => {
                      let result = hit._highlightResult[key] || {},
                        html = result.value;

                      if (result.length) {
                        html = result.map(r => r.value).join(", ");
                      }

                      // Fallback if not in _highlightResult
                      if (!html) {
                        html = get(hit, key);
                      }

                      return (
                        <span
                          key={i}
                          dangerouslySetInnerHTML={{ __html: html }}
                        />
                      );
                    })
                    .concat(extraSubtitleItems),
                  " · "
                )}
              </div>
              {godMode ? (
                <div style={HIT_SUBTITLE_STYLE}>
                  {
                    (
                      condensedUsers.find(user => user.id === hit._owner) || {
                        organization: "????"
                      }
                    ).organization
                  }
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    ) : null;
  }
}

export default connect(state => ({ results: state.searchResults }))(
  SearchResults
);



// WEBPACK FOOTER //
// ./src/app/components/partials/search/global-search-results.jsx